<template>
  <v-dialog v-model="showDialog" max-width="980" :fullscreen="isPhoneBool" scrollable @click:outside="hide">
    <v-card class="part-info-dialog">
      <div class="dialog-header">
        <div class="header-content">
          <div class="eyebrow-row">
            <v-icon size="18" color="primary">mdi-package-variant-closed</v-icon>
            <span>{{ $t("text.article_number") }}</span>
            <strong v-if="displayArticleNumber">{{ displayArticleNumber }}</strong>
          </div>
          <h2 class="dialog-title">{{ dialogTitle }}</h2>
        </div>

        <v-btn icon variant="text" color="error" class="close-button" :aria-label="$t('text.close')" @click="hide">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text v-if="loading" class="dialog-state">
        <v-progress-circular indeterminate color="primary" size="36" width="3"></v-progress-circular>
        <span>{{ $t("text.loading") }}...</span>
      </v-card-text>

      <v-card-text v-else-if="loadError" class="dialog-state dialog-state--error">
        <v-icon color="error" size="34">mdi-alert-circle-outline</v-icon>
        <span>{{ $t("text.none_available") }}</span>
      </v-card-text>

      <v-card-text v-else-if="Part" class="dialog-body">
        <section class="image-panel">
          <div class="image-frame">
            <ArticleImage :size="imageSize" :relativePath="Part && Part.ArticleImage"></ArticleImage>
          </div>
        </section>

        <section class="detail-panel">
          <div class="section-heading">
            <v-icon size="18" color="primary">mdi-text-box-outline</v-icon>
            <span>{{ $t("text.description") }}</span>
          </div>

          <div class="description-stack">
            <p v-if="Part.ArticleDescription" class="lead-description">{{ Part.ArticleDescription }}</p>
            <p v-if="Part.ArticleDescriptionLong" class="body-description">{{ Part.ArticleDescriptionLong }}</p>
            <p v-if="Part.SalesText" class="body-description">{{ Part.SalesText }}</p>
            <p v-if="!hasDescription" class="empty-text">{{ $t("text.none_available") }}</p>
          </div>

          <div class="info-grid">
            <div class="info-row">
              <span>{{ $t("text.article_number") }}</span>
              <strong>{{ displayArticleNumber || "-" }}</strong>
            </div>
            <div v-if="showPrice" class="info-row">
              <span>{{ $t("text.price") }}</span>
              <strong>{{ priceLabel || "-" }}</strong>
            </div>
          </div>
        </section>
      </v-card-text>

      <v-divider />

      <v-card-actions class="dialog-actions">
        <v-btn variant="outlined" @click="hide">
          {{ $t("text.back") }}
        </v-btn>

        <v-spacer />

        <div class="quantity-stepper" :aria-label="$t('text.amount')">
          <v-btn icon size="small" variant="text" :disabled="quantityValue <= 1 || !Part" @click="changeQuantity(-1)">
            <v-icon size="18">mdi-minus</v-icon>
          </v-btn>

          <v-text-field v-model.number="quantity" class="quantity-input" hide-details density="compact" variant="plain"
            type="number" min="1" :aria-label="$t('text.amount')" :disabled="!Part" @blur="normalizeQuantity">
          </v-text-field>

          <v-btn icon size="small" variant="text" color="primary" :disabled="!Part" @click="changeQuantity(1)">
            <v-icon size="18">mdi-plus</v-icon>
          </v-btn>
        </div>

        <v-btn color="primary" class="add-button" :disabled="!Part || isInAsync" :loading="isInAsync"
          @click="addToCart">
          <v-icon class="mr-1">mdi-cart-plus</v-icon>
          {{ $t("text.add") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import http from "@/api/client";
import ArticleImage from "@/components/Cards/ArticleImage.vue";
import AuthStoreMixin from "@/helpers/mixins/AuthStoreMixin";

export default {
  name: "PartInfoDialog",
  mixins: [AuthStoreMixin],
  components: {
    ArticleImage,
  },

  data() {
    return {
      showDialog: true,
      Part: null,
      quantity: 1,
      loading: false,
      loadError: null,
      requestId: 0,
      closing: false,
    };
  },

  mounted() {
    this.init();
  },

  methods: {
    async init() {
      const part = this.$route.params.part;
      this.closing = false;
      this.showDialog = true;
      this.Part = null;
      this.quantity = 1;
      this.loadError = null;

      if (!part) {
        return;
      }

      const activeRequest = ++this.requestId;
      this.loading = true;

      await http()
        .get("api2/customer/article/part/" + encodeURIComponent(part))
        .then((response) => {
          if (activeRequest !== this.requestId) return;
          this.Part = {
            ...response.data,
            Quantity: 1,
          };
        })
        .catch((error) => {
          if (activeRequest !== this.requestId) return;
          this.loadError = error;
        })
        .finally(() => {
          if (activeRequest === this.requestId) {
            this.loading = false;
          }
        });
    },

    addToCart() {
      if (!this.Part) return;

      const cartItem = {
        ...this.Part,
        MachineId: this.$route.params.id,
        Quantity: this.quantityValue,
      };
      this.addItemToCart(cartItem);
    },

    changeQuantity(increment) {
      this.quantity = Math.max(1, this.quantityValue + increment);
    },

    normalizeQuantity() {
      this.quantity = this.quantityValue;
    },

    hide() {
      if (this.closing) return;

      this.closing = true;
      this.showDialog = false;

      const id = this.$route.params.id;
      if (!id) {
        this.closing = false;
        return;
      }

      this.$router.replace({
        name: "PartsPanel",
        params: { id: String(id) },
        query: { ...this.$route.query },
      });
    },
  },

  computed: {
    isInAsync() {
      return this.shoppingCartBusy;
    },

    showPrice() {
      return this.canShowPrices;
    },

    displayArticleNumber() {
      return (this.Part && this.Part.ArticleNumber) || this.$route.params.part || "";
    },

    dialogTitle() {
      if (this.Part && this.Part.ArticleDescription) return this.Part.ArticleDescription;
      return this.displayArticleNumber || this.$t("text.loading");
    },

    hasDescription() {
      return Boolean(this.Part && (this.Part.ArticleDescription || this.Part.ArticleDescriptionLong || this.Part.SalesText));
    },

    priceLabel() {
      const price = this.priceValue;
      if (price == null || price === "" || price === 0) return "";

      const currency = this.authUserInfo && this.authUserInfo.Currency ? " " + this.authUserInfo.Currency : "";
      return this.formatCurrency(price) + currency;
    },

    priceValue() {
      if (!this.Part) return null;
      if (this.Part.HasCustomerPrice && this.Part.CustomerArticlePrice !== "" && this.Part.CustomerArticlePrice != null) {
        return this.Part.CustomerArticlePrice;
      }

      return this.Part.ArticlePrice;
    },

    quantityValue() {
      return Math.max(1, Number(this.quantity) || 1);
    },

    imageSize() {
      return this.isPhoneBool ? 160 : 220;
    },
  },

  watch: {
    "$route.params.part"() {
      this.init();
    },

    showDialog(value) {
      if (!value && this.$route.params.part && !this.closing) {
        this.hide();
      }
    },
  },
};
</script>

<style scoped>
.part-info-dialog {
  border-radius: 8px;
  overflow: hidden;
}

.dialog-header {
  align-items: flex-start;
  background: #ffffff;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 20px 24px 18px;
}

.header-content {
  min-width: 0;
}

.eyebrow-row {
  align-items: center;
  color: rgba(0, 0, 0, .58);
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  font-weight: 700;
  gap: 7px;
}

.eyebrow-row strong {
  color: rgba(0, 0, 0, .78);
  overflow-wrap: anywhere;
}

.dialog-title {
  color: rgba(0, 0, 0, .87);
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  margin: 8px 0 0;
  overflow-wrap: anywhere;
}

.close-button {
  flex: 0 0 auto;
}

.dialog-state {
  align-items: center;
  background: #f7f8fa;
  color: rgba(0, 0, 0, .62);
  display: flex;
  font-size: 14px;
  font-weight: 600;
  gap: 14px;
  justify-content: center;
  min-height: 260px;
}

.dialog-state--error {
  color: rgba(0, 0, 0, .68);
}

.dialog-body {
  background: #f7f8fa;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  max-height: 72vh;
  overflow-y: auto;
  padding: 16px;
}

.image-panel,
.detail-panel {
  background: #ffffff;
  border: 1px solid #e5e8ef;
  border-radius: 8px;
}

.image-panel {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 280px;
  padding: 18px;
}

.image-frame {
  align-items: center;
  background: #f3f5f7;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 8px;
  display: flex;
  justify-content: center;
  max-width: 100%;
  overflow: hidden;
  padding: 12px;
}

.detail-panel {
  padding: 16px;
}

.section-heading {
  align-items: center;
  color: rgba(0, 0, 0, .78);
  display: flex;
  font-size: 13px;
  font-weight: 700;
  gap: 8px;
  margin-bottom: 12px;
}

.description-stack {
  display: grid;
  gap: 10px;
}

.lead-description,
.body-description,
.empty-text {
  margin: 0;
  overflow-wrap: anywhere;
}

.lead-description {
  color: rgba(0, 0, 0, .86);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
}

.body-description {
  color: rgba(0, 0, 0, .66);
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
}

.empty-text {
  color: rgba(0, 0, 0, .54);
  font-size: 13px;
}

.info-grid {
  border-top: 1px solid #edf0f4;
  display: grid;
  gap: 10px;
  margin-top: 18px;
  padding-top: 14px;
}

.info-row {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.info-row span {
  color: rgba(0, 0, 0, .56);
  font-size: 12px;
}

.info-row strong {
  color: rgba(0, 0, 0, .86);
  font-size: 13px;
  text-align: right;
  overflow-wrap: anywhere;
}

.dialog-actions {
  background: #ffffff;
  gap: 12px;
  padding: 12px 16px;
}

.quantity-stepper {
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 8px;
  display: flex;
  height: 40px;
  overflow: hidden;
}

.quantity-input {
  flex: 0 0 48px;
  max-width: 48px;
}

.quantity-input :deep(input) {
  font-size: 14px;
  font-weight: 700;
  min-height: 36px;
  padding: 0;
  text-align: center;
}

.quantity-input :deep(input::-webkit-inner-spin-button),
.quantity-input :deep(input::-webkit-outer-spin-button) {
  margin: 0;
}

.add-button {
  min-width: 108px;
}

@media (max-width: 700px) {
  .part-info-dialog {
    border-radius: 0;
    min-height: 100%;
  }

  .dialog-header {
    padding: 16px;
  }

  .dialog-title {
    font-size: 19px;
  }

  .dialog-body {
    grid-template-columns: 1fr;
    max-height: none;
    padding: 12px;
  }

  .image-panel {
    min-height: 210px;
  }

  .dialog-actions {
    align-items: stretch;
    flex-wrap: wrap;
  }

  .quantity-stepper {
    margin-left: auto;
  }

  .add-button {
    flex: 1 1 100%;
  }
}
</style>